// src/components/Home.js
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import SearchForm from "./Search/SearchForm"; // Adjust path as needed
import CompactSearchForm from "./Search/CompactSearchForm";
import Results from "./Results";
import { Container, Typography, Box, CircularProgress, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, PickersDay } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDebounce } from "use-debounce";
import rawCitiesData from "../iata_cities.json"; // Adjust path as needed
import { styled } from "@mui/material/styles";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // Import carousel styles
import HowItWorks from "./HowItWorks";
import Explainer from "./Explainer";
import ContactForm from "./ContactForm";
import Subscribe from "./Subscribe";
import { Divider, Button } from "@mui/material";
import { useLocation } from "react-router-dom";
import { darken } from "@mui/system";
import { supabase, getAuthHeader } from "../utils/supabaseClient";
import { logger } from "../utils/logger";
import { canUseFeature, incrementUsage } from "../utils/usageLimit";
import { FREE_LIMITS } from "../utils/limits";
import ProUpgradeDialog from "./ProUpgradeDialog";
import { Helmet } from "react-helmet-async";
import ExploreCityCarousel from "./Search/ExploreCityCarousel"; // Adjust path if needed
import ExploreCompactSearchForm from "./Search/ExploreCompactSearchForm";
import { resolveCityFromIATA } from "../utils/resolveCityFromIATA";
import { useOutletContext } from "react-router-dom";
import { filterAirbnbResults } from "../utils/filterAirbnbResults";
import {
  buildSearchParams,
  paramsToQueryString,
  parseSearchParams,
  findCityByKey,
  describeSearch,
  canonicalKeyFromParams,
  canonicalKeyFromState,
} from "../utils/searchUrlState";
import { reportTiming } from "../utils/reportTiming";
import SavedSearchesDialog from "./SavedSearchesDialog";
import { Snackbar, IconButton as MuiIconButton } from "@mui/material";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// ─── SerpAPI Backup Toggle ───────────────────────────────────────────────────
const SERPAPI_FALLBACK_ENABLED = false; // Master toggle — set false to disable SerpAPI backup

// ─── API Limit Alert ─────────────────────────────────────────────────────────
// Sends a one-time email alert per session when a rate/quota limit is hit.
const _alertedApis = new Set();
async function sendApiAlert({ api, statusCode, message, endpoint }) {
  const key = `${api}-${statusCode}`;
  if (_alertedApis.has(key)) return; // don't spam
  _alertedApis.add(key);
  try {
    await supabase.functions.invoke("send-api-alert", {
      body: { api, statusCode, message, endpoint },
    });
  } catch (e) {
    console.warn("sendApiAlert failed:", e);
  }
}

const formatSerpDuration = (mins) => {
  if (!mins) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h} hr ${m} min` : `${m} min`;
};

// Normalize raw google-flights2 legs to the same shape as outbound `legs`
const normalizeReturnLegs = (rawLegs = []) =>
  rawLegs.map((leg) => ({
    flightNumber: leg.flight_number,
    aircraft: leg.aircraft,
    airline: leg.airline,
    airlineLogo: leg.airline_logo,
    departure: {
      code: leg.departure_airport?.airport_code,
      city: leg.departure_airport?.airport_name,
      airportName: leg.departure_airport?.airport_name,
      time: leg.departure_airport?.time,
    },
    arrival: {
      code: leg.arrival_airport?.airport_code,
      city: leg.arrival_airport?.airport_name,
      airportName: leg.arrival_airport?.airport_name,
      time: leg.arrival_airport?.time,
    },
    durationRaw: leg.duration?.raw,
    durationText: leg.duration?.text,
    legroom: leg.legroom,
    extensions: leg.extensions,
  }));

const CustomPickersDayStyled = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "departureDate" && prop !== "key", // Exede 'key'
})(({ theme, departureDate, day }) => ({
  ...(departureDate &&
    day.getTime() === departureDate.getTime() && {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
      },
    }),
  "&.Mui-selected": {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
  },
}));

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State variables
  const [originInput, setOriginInput] = useState("");
  const [destinationInput, setDestinationInput] = useState("");
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0); // Add children state
  const [flightResults, setFlightResults] = useState([]); // Default to empty array
  const [flightPriceHistory, setFlightPriceHistory] = useState(null);
  const [noFlightsFound, setNoFlightsFound] = useState(false); // New state
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("best");
  const [searchInitiated, setSearchInitiated] = useState(false); // Add this new state
  const [selectedStayCategory, setSelectedStayCategory] = useState("standard"); // Default to 'standard'
  const [economyListings, setEconomyListings] = useState([]);
  const [standardListings, setStandardListings] = useState([]);
  const [premiumListings, setPremiumListings] = useState([]);
  const [flightType, setFlightType] = useState("roundtrip");
  const [airbnbType, setAirbnbType] = useState("both"); // Default value is 'both'
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [submittedOrigin, setSubmittedOrigin] = useState(null);
  const [submittedDestination, setSubmittedDestination] = useState(null);
  const [originIATA, setOriginIATA] = useState(null);
  const [destIATA, setDestIATA] = useState(null);
  const [submittedDepartureDate, setSubmittedDepartureDate] = useState(null);
  const [submittedReturnDate, setSubmittedReturnDate] = useState(null);
  const [noAirbnbFound, setNoAirbnbFound] = useState(false);
  const location = useLocation();
  const [searchType, setSearchType] = useState("flightsAndStays");
  const [minReviewCount, setMinReviewCount] = useState(5); // Default to 5
  const [minReviewScore, setMinReviewScore] = useState("4.0");
  const [exploreResults, setExploreResults] = useState([]); // ✅ NEW
  const [exploreLoading, setExploreLoading] = useState(false);
  const [cityStayLoading, setCityStayLoading] = useState({});
  const [isExploring, setIsExploring] = useState(false);
  const [prefetchedExploreStays, setPrefetchedExploreStays] = useState({});
  const [prefetchedExploreMedians, setPrefetchedExploreMedians] = useState({});
  const [prefetchedExploreFlights, setPrefetchedExploreFlights] = useState({});
  const [allFetchedListings, setAllFetchedListings] = useState([]);
  const [flightRetryAttempt, setFlightRetryAttempt] = useState(0);
  const [tofly, setToFly] = useState(false);
  const [exploreFlightPrefs, setExploreFlightPrefs] = useState(null);
  const [selectedExploreCity, setSelectedExploreCity] = useState(null);
  const [flightLoading, setFlightLoading] = useState(false);
  const [stayLoading, setStayLoading] = useState(false);
  const [transportMode, setTransportMode] = useState("flight");
  const [travelTime, setTravelTime] = useState(6); // Default max travel time in hours
  const [preferences, setPreferences] = useState(["Any"]);
  const [destinationVibe, setDestinationVibe] = useState("typical");
  const [aiDescription, setAiDescription] = useState("");
  const { userCurrency, setUserCurrency, isPro } = useOutletContext();
  const [limitDialog, setLimitDialog] = useState(null);
  const resultsRef = useRef(null);
  const cityCarouselRef = useRef(null);
  const currentSearchIdRef = useRef(0);
  const currentStaySearchIdRef = useRef(0);
  // Track previous loading states to detect load-complete transitions
  const prevLoadingRef = useRef(false);
  const prevExploreLoadingRef = useRef(false);

  // Shareable / saveable search state
  const [savedSearchesOpen, setSavedSearchesOpen] = useState(false);
  const [toast, setToast] = useState(null); // { message, severity }
  // List of the user's current saved searches as { id, canonicalKey } — used
  // to detect whether the current search is already saved (drives the bookmark
  // filled / outline state).
  const [savedSearchIndex, setSavedSearchIndex] = useState([]);
  const hydratedFromUrlRef = useRef(false);
  const pendingAutoRunRef = useRef(null); // search payload to run after state settles

  useEffect(() => {
    if (transportMode !== "flight") {
      setToFly(false); // Auto-uncheck when switching away from flight mode
    }
  }, [transportMode]);

  // Mobile-only: keep the viewport anchored at the top of the page on both the
  // search-start and results-loaded transitions. The hero band collapsing to
  // display:none (and back) otherwise leaves the page scrolled down to the footer.
  useEffect(() => {
    if (!isMobile || !searchInitiated || searchType === "explore") return;
    const wasLoading = prevLoadingRef.current;
    const isLoading = loading || stayLoading;
    prevLoadingRef.current = isLoading;
    if (wasLoading !== isLoading) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [loading, stayLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile-only: scroll to city carousel when explore results finish loading
  useEffect(() => {
    if (!isMobile || !searchInitiated || searchType !== "explore") return;
    const wasLoading = prevExploreLoadingRef.current;
    prevExploreLoadingRef.current = exploreLoading;
    if (wasLoading && !exploreLoading && cityCarouselRef.current) {
      cityCarouselRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [exploreLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!searchInitiated) return;
    if (!allFetchedListings || allFetchedListings.length === 0) return;

    handleStaySearch(
      advancedFilters, // new filters
      destination, // keep the same
      allFetchedListings, // reuse listings you already fetched
      departureDate,
      returnDate,
      userCurrency
    );
  }, [advancedFilters]);

  useEffect(() => {
    logger.log(
      "[Home] loading:",
      loading,
      "flightLoading:",
      flightLoading,
      "stayLoading:",
      stayLoading,
      "exploreLoading:",
      exploreLoading,
      "searchType:",
      searchType,
      "searchInitiated:",
      searchInitiated
    );
  }, [
    loading,
    flightLoading,
    stayLoading,
    exploreLoading,
    searchType,
    searchInitiated,
  ]);

  useEffect(() => {
    // leave explore-mode only when you go back to the empty search form
    if (!searchInitiated) {
      setIsExploring(false);
    }
  }, [searchInitiated]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const scrollTo = params.get("scrollTo");

    if (scrollTo === "contact") {
      const contactSection = document.querySelector("#contact");
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: "smooth" });
      }

      // Clear the query parameter from URL without reloading the page
      const newUrl = window.location.pathname;
      window.history.replaceState(null, "", newUrl);
    }
  }, [location.search]);

  // Handle stay category change
  const handleStayCategoryChange = (event, newValue) => {
    if (newValue !== null) {
      setSelectedStayCategory(newValue);
    }
  };

  // Format date for SkyScanner URL
  const formatDateForSkyScanner = (date) => {
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}${month}${day}`;
  };

  // Generate SkyScanner link
  const generateSkyScannerLink = useCallback(() => {
    if (!origin || !destination || !departureDate) return "#";

    const formattedDepartureDate = formatDateForSkyScanner(departureDate);
    const originIATA = origin.iata;
    const destinationIATA = destination.iata;

    let skyscannerUrl = `https://www.skyscanner.com/transport/flights/${originIATA}/${destinationIATA}/${formattedDepartureDate}`;

    // Add return date only if it's a roundtrip
    if (flightType === "roundtrip" && returnDate) {
      const formattedReturnDate = formatDateForSkyScanner(returnDate);
      skyscannerUrl += `/${formattedReturnDate}`;
    }
    // Create query parameters
    const queryParams = new URLSearchParams({
      adults: adults.toString(), // SkyScanner sometimes needs this
      adultsv2: adults.toString(), // Required for correct search
      cabinclass: "economy",
      infants: "0", // No infants by default
      preferdirects: "false",
      outboundaltsenabled: "true",
      inboundaltsenabled: "true",
      ref: "home",
      rtn: flightType === "roundtrip" ? "1" : "0",
    });

    // Only add children if children > 0
    if (children > 0) {
      const childrenAges = Array(children).fill("4").join("|"); // Use "|" as separator
      queryParams.append("children", children.toString());
      queryParams.append("childrenv2", childrenAges);
    }

    // Return final URL
    return `${skyscannerUrl}/?${queryParams.toString()}`;
  }, [
    origin,
    destination,
    departureDate,
    returnDate,
    adults,
    children,
    flightType,
  ]);

  // Filter and sort airports data
  // 1) keep your 40k-entry list in state (only once)
  const citiesData = useMemo(() => rawCitiesData, []);

  // 2) filter & sort by population, cap at 50 results
  const filterCities = useCallback(
    (inputValue) => {
      const normalize = (str) =>
        str
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();

      const results = [];
      const addedCityIdentifiers = new Set(); // Reset for each call

      const addCityToResults = (city) => {
        const cityId = `${city.city}|${city.admin}|${city.country_code}`;
        if (!addedCityIdentifiers.has(cityId)) {
          results.push(city);
          addedCityIdentifiers.add(cityId);
        }
      };

      if (!inputValue || typeof inputValue !== "string") {
        return [];
      }

      const q = normalize(inputValue.trim());

      // --- Priority 1: IATA Code Match ---
      // (This part remains the same - ensure it's working based on earlier debugging)
      if (q.length === 3) {
        const iataMatches = citiesData
          .filter(
            (city) =>
              city.airports &&
              Array.isArray(city.airports) &&
              city.airports.some((airport) => {
                if (airport.iata && typeof airport.iata === "string") {
                  const normalizedIataFromData = normalize(airport.iata.trim());
                  return normalizedIataFromData === q;
                }
                return false;
              })
          )
          .sort((a, b) => b.population - a.population);
        iataMatches.forEach(addCityToResults);
      }

      // --- Priority 2: City name starts with the query ---
      // (This part remains the same)
      const cityStartsWithMatches = citiesData
        .filter((city) => city.city && normalize(city.city).startsWith(q)) // Added city.city check for safety
        .sort((a, b) => b.population - a.population);
      cityStartsWithMatches.forEach(addCityToResults);

      // --- Priority 3: Label contains ANY of the query words (in any order) ---
      // MODIFIED PART: Changed .every to .some
      const queryWords = q.length > 0 ? q.split(" ").filter(Boolean) : [];
      if (queryWords.length > 0) {
        const labelContainsAnyWordMatches = citiesData // Renamed for clarity
          .filter(({ city, admin, country_name }) => {
            const labelText = normalize(
              [city, admin, country_name].filter(Boolean).join(" ")
            );
            // If any word from the query is found in the labelText, it's a match
            return queryWords.some((word) => labelText.includes(word));
          })
          .sort((a, b) => b.population - a.population);
        labelContainsAnyWordMatches.forEach(addCityToResults);
      }

      return results.slice(0, 50);
    },
    [citiesData] // Make sure citiesData is stable or memoized
  );

  // 3) debounce + generate your options
  const [debouncedOriginInput] = useDebounce(originInput, 300);
  const [debouncedDestinationInput] = useDebounce(destinationInput, 300);

  const originOptions = useMemo(
    () => filterCities(debouncedOriginInput),
    [debouncedOriginInput, filterCities]
  );
  const destinationOptions = useMemo(
    () => filterCities(debouncedDestinationInput),
    [debouncedDestinationInput, filterCities]
  );

  // Cache entity IDs in localStorage with key `entityIdsCache`
  const loadEntityIdsCache = () =>
    JSON.parse(localStorage.getItem("entityIdsCache")) || {};
  const saveEntityIdsCache = (cache) =>
    localStorage.setItem("entityIdsCache", JSON.stringify(cache));

  const getCityId = useCallback(async (cityObject) => {
    if (!cityObject) {
      logger.error("getCityId: missing cityObject");
      return null;
    }
    // Use iata_cities.json as fallback if standard keys are missing
    if (!cityObject.admin || !cityObject.country_code) {
      const { city, country } = cityObject;
      logger.log(`🔍 Attempting city/country lookup: ${city}, ${country}`);

      const matchExact = rawCitiesData.find(
        (entry) =>
          entry.city.toLowerCase() === city?.toLowerCase() &&
          entry.country_name.toLowerCase() === country?.toLowerCase()
      );

      const matchCityOnly = rawCitiesData.find(
        (entry) => entry.city.toLowerCase() === city?.toLowerCase()
      );

      const match = matchExact || matchCityOnly;

      if (match) {
        // If a local match is found, re-run the function with the complete city object.
        // This is the cleanest way to send it down the main logic path.
        return getCityId(match);
      } else {
        // If no local match, call the API directly, exactly as you suggested.
        logger.warn(
          `❌ No match in iata_cities.json for ${city}, ${country}. Trying direct API lookup.`
        );
        try {
          // This is a direct copy of the API call from the main logic block
          const resp = await axios.get(
            "https://google-flights2.p.rapidapi.com/api/v1/searchAirport",
            {
              params: {
                query: `${city}, ${country}`, // Using only city and country
                language_code: "en-US",
              },
              headers: {
                "x-rapidapi-key": process.env.REACT_APP_RAPIDAPI_KEY,
                "x-rapidapi-host": "google-flights2.p.rapidapi.com",
              },
            }
          );

          const list = resp.data?.data;
          if (Array.isArray(list) && list.length) {
            const apiMatch = list.find(
              (e) =>
                e.title?.toLowerCase().startsWith(city.toLowerCase()) &&
                e.type !== "airport"
            );
            const fetchedCityId = (apiMatch || list[0]).id;
            logger.log(`✓ Direct API lookup success: ${fetchedCityId}`);
            return fetchedCityId;
          }
          return null;
        } catch (e) {
          logger.error("RapidAPI error on fallback:", e.message || e);
          return null;
        }
      }
    }

    // Original logic (Supabase + RapidAPI)
    const { city, admin, country_code, country_name } = cityObject;
    const cacheKey = `${city}|${admin}|${country_code}`.toLowerCase();
    const cache = loadEntityIdsCache();

    if (cache[cacheKey]) {
      logger.log(`✓ Cache hit: ${cacheKey} → ${cache[cacheKey]}`);
      return cache[cacheKey];
    }

    logger.log(`Querying Supabase for ${city}, ${admin}, ${country_code}`);
    const { data: row, error: selErr } = await supabase
      .from("cities")
      .select("id, city_id")
      .eq("city", city)
      .eq("admin", admin)
      .eq("country_code", country_code)
      .limit(1)
      .maybeSingle();

    if (selErr) {
      logger.error("Supabase select error:", selErr.message);
      return null;
    }
    if (!row) {
      logger.error(`No row for ${city}, ${admin}, ${country_code}`);
      return null;
    }

    if (row.city_id) {
      logger.log(`✓ Supabase hit: ${row.city_id}`);
      cache[cacheKey] = row.city_id;
      saveEntityIdsCache(cache);
      return row.city_id;
    }

    logger.log(`🏃‍♂️ Fetching from Google-Flights API for ${city}`);
    let fetchedCityId = null;
    try {
      const resp = await axios.get(
        "https://google-flights2.p.rapidapi.com/api/v1/searchAirport",
        {
          params: {
            query: ["US", "CA"].includes(country_code)
              ? `${city}, ${admin}, ${country_code}`
              : `${city}, ${country_name}`,
            language_code: "en-US",
          },
          headers: {
            "x-rapidapi-key": process.env.REACT_APP_RAPIDAPI_KEY,
            "x-rapidapi-host": "google-flights2.p.rapidapi.com",
          },
        }
      );

      const list = resp.data?.data;
      if (Array.isArray(list) && list.length) {
        const match = list.find(
          (e) =>
            e.title?.toLowerCase().startsWith(city.toLowerCase()) &&
            e.type !== "airport"
        );
        fetchedCityId = (match || list[0]).id;
      }
    } catch (e) {
      logger.error("RapidAPI error:", e.message || e);
      return null;
    }

    if (!fetchedCityId) {
      logger.warn(`No city_id returned for ${city}`);
      return null;
    }

    const { error: updErr } = await supabase
      .from("cities")
      .update({ city_id: fetchedCityId })
      .eq("id", row.id);

    if (updErr) {
      logger.error("Supabase update error:", updErr.message);
    } else {
      logger.log(`✓ Updated row ${row.id} with city_id ${fetchedCityId}`);
      cache[cacheKey] = fetchedCityId;
      saveEntityIdsCache(cache);
    }

    return fetchedCityId;
  }, []);

  // Lazy-load return flights for a single itinerary (used by the expanded flights column).
  // Fetches getNextFlights on demand, normalizes, updates flightResults state, returns the enriched itinerary.
  const enrichItineraryReturn = useCallback(
    async (itinerary) => {
      if (!itinerary?.nextToken) return itinerary;
      if (itinerary.returnLegs && itinerary.returnLegs.length > 0) return itinerary;
      try {
        const ret = await axios.request({
          method: "GET",
          url: "https://google-flights2.p.rapidapi.com/api/v1/getNextFlights",
          params: {
            next_token: itinerary.nextToken,
            show_hidden: "1",
            currency: userCurrency || "USD",
            language_code: "en-US",
            country_code: "US",
          },
          headers: {
            "x-rapidapi-key": process.env.REACT_APP_RAPIDAPI_KEY,
            "x-rapidapi-host": "google-flights2.p.rapidapi.com",
          },
          timeout: 30000,
        });

        const retData = ret.data?.data?.itineraries;
        const chosenRet =
          retData?.topFlights?.[0] || retData?.otherFlights?.[0] || null;

        if (!chosenRet) {
          logger.warn("⚠️ Lazy return enrichment: no return flights found");
          return itinerary;
        }

        const enriched = {
          ...itinerary,
          returnLegs: normalizeReturnLegs(chosenRet?.flights),
          returnOverallDurationText: chosenRet?.duration?.text,
          returnOverallDurationRaw: chosenRet?.duration?.raw,
          bookingToken: chosenRet?.booking_token || itinerary.bookingToken,
        };

        setFlightResults((prev) =>
          prev.map((f) => (f.id === itinerary.id ? enriched : f))
        );
        return enriched;
      } catch (err) {
        logger.warn("❌ Lazy return enrichment failed:", err?.message || err);
        return itinerary;
      }
    },
    [userCurrency]
  );

  const handleClearStayFilter = () => {
    setAirbnbType("both");
    setAdvancedFilters({});
    setMinReviewCount(5);
    setMinReviewScore(4.0);
    setSelectedStayCategory("standard");

    if (allFetchedListings.length > 0) {
      const resetFilteredResults = filterAirbnbResults(allFetchedListings, {
        airbnbType: "both",
        minReviewCount: 5,
        minReviewScore: 4.0,
        advancedFilters: {},
      });

      if (resetFilteredResults.length < 9) {
        setEconomyListings([]);
        setStandardListings(resetFilteredResults);
        setPremiumListings([]);
        setQuartiles({ Q1: null, Q2: null, Q3: null });
      } else {
        const totalPrices = resetFilteredResults.map((r) => r.price.total);
        const calculatedQuartiles = calculateQuartiles(totalPrices);
        setQuartiles(calculatedQuartiles);
        const { economyListings, standardListings, premiumListings } =
          classifyAndSortListingsByPrice(
            resetFilteredResults,
            calculatedQuartiles
          );

        setEconomyListings(economyListings);
        setStandardListings(standardListings);
        setPremiumListings(premiumListings);
      }
    }
  };

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Function to fetch Airbnb stays, memoized with useCallback
  const fetchAirbnbStays = useCallback(
    async (destination, currency) => {
      const location = destination.admin
        ? `${destination.city}, ${destination.admin}, ${destination.country_name}`
        : `${destination.city}, ${destination.country_name}`;
      const checkinDate = formatLocalDate(departureDate);
      const checkoutDate = formatLocalDate(returnDate);

      const isDefaultFilters =
        (advancedFilters.minPrice == null || advancedFilters.minPrice === 0) &&
        (advancedFilters.maxPrice == null ||
          advancedFilters.maxPrice === 300) &&
        (!advancedFilters.cancelPolicies ||
          advancedFilters.cancelPolicies.length === 0) &&
        Object.keys(advancedFilters).filter(
          (k) => k.startsWith("amenity_") && advancedFilters[k]
        ).length === 0 &&
        minReviewCount === 5 &&
        parseFloat(minReviewScore) === 4.0;

      const maxPages = isDefaultFilters ? 4 : 5;
      const allResults = [];

      const fetchPage = async (page) => {
        const options = {
          method: "GET",
          url: "https://airbnb13.p.rapidapi.com/search-location",
          params: {
            location,
            checkin: checkinDate,
            checkout: checkoutDate,
            adults: String(adults),
            children: String(children),
            infants: "0",
            pets: "0",
            page: String(page),
            currency: currency || "USD",
          },
          headers: {
            "x-rapidapi-key": process.env.REACT_APP_RAPIDAPI_KEY,
            "x-rapidapi-host": "airbnb13.p.rapidapi.com",
          },
        };

        try {
          const response = await axios.request(options);
          logger.log(`📄 Airbnb Response for page ${page}:`, response.data);
          return response.data.results || [];
        } catch (error) {
          logger.error(`❌ Error fetching page ${page}:`, error);
          const status = error.response?.status;
          if (status === 429 || status === 403 || status === 402) {
            sendApiAlert({
              api: "airbnb",
              statusCode: status,
              message: error.response?.data?.message || error.message,
              endpoint: "airbnb13.p.rapidapi.com/search-location",
            });
          }
          return [];
        }
      };

      for (let page = 1; page <= maxPages; page++) {
        const pageResults = await fetchPage(page);
        if (pageResults.length === 0) break;

        const newResults = pageResults.filter(
          (result) => !allResults.some((r) => r.id === result.id)
        );
        allResults.push(...newResults);

        logger.log(
          `📦 Total listings after page ${page}: ${allResults.length}`
        );
      }

      return allResults;
    },
    [
      departureDate,
      returnDate,
      adults,
      children,
      airbnbType,
      minReviewCount,
      minReviewScore,
      advancedFilters,
    ]
  );

  const normalizeListingPrices = useCallback((listings, nights) => {
    return listings.map((listing) => {
      if (!listing.price) return listing;

      const price = listing.price;

      // fallback if total is null or zero
      if (!price.total && Array.isArray(price.priceItems)) {
        // try to get the first priceItem with an amount
        const firstItemWithAmount = price.priceItems.find(
          (item) => typeof item.amount === "number"
        );
        if (firstItemWithAmount) {
          price.total = firstItemWithAmount.amount;
        } else {
          // try to parse from a title like "$112 x 4 nights"
          const nightItem = price.priceItems.find((item) =>
            item.title.toLowerCase().includes("night")
          );
          const match = nightItem?.title.match(
            /\$([\d.]+)\s*x\s*(\d+)\s*nights?/i
          );
          if (match) {
            const rate = parseFloat(match[1]);
            const count = parseInt(match[2], 10);
            price.total = rate * count;
          } else {
            price.total = 0; // fallback so it's numeric
          }
        }
      }

      return listing;
    });
  }, []);

  // Calculate tier boundaries using the 5th–95th percentile range so that
  // outliers (a $10 closet or a $3000 mansion) don't skew the thresholds for
  // the rest of the listings. Returns lowerBound (economy/standard cut) and
  // upperBound (standard/premium cut).
  const calculateQuartiles = useCallback((numbers) => {
    const valid = numbers.filter((p) => p != null && !isNaN(p) && p > 0);
    if (valid.length === 0) return { Q1: null, Q2: null, Q3: null, lowerBound: null, upperBound: null };

    const sorted = valid.slice().sort((a, b) => a - b);

    const percentile = (p) => {
      const idx = (p / 100) * (sorted.length - 1);
      const lo = Math.floor(idx);
      const hi = Math.ceil(idx);
      return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
    };

    // Trim outliers: use the 5th–95th percentile range for boundary calculation
    const p5  = percentile(5);
    const p95 = percentile(95);
    const trimmed = sorted.filter((p) => p >= p5 && p <= p95);
    const base = trimmed.length >= 3 ? trimmed : sorted; // fall back if too few remain

    const basePercentile = (p) => {
      const idx = (p / 100) * (base.length - 1);
      const lo = Math.floor(idx);
      const hi = Math.ceil(idx);
      return base[lo] + (base[hi] - base[lo]) * (idx - lo);
    };

    const lowerBound = basePercentile(33); // economy / standard cut
    const upperBound = basePercentile(67); // standard / premium cut
    const median     = basePercentile(50);

    return { Q1: lowerBound, Q2: median, Q3: upperBound, lowerBound, upperBound };
  }, []);

  // Classify every listing against the price thresholds.
  // Because we compare price directly (not array membership) every listing
  // gets exactly one tier — no duplicates, no dropouts.
  // Outliers naturally land in economy (very cheap) or premium (very expensive).
  const classifyAndSortListingsByPrice = useCallback((listings, quartiles) => {
    if (listings.length < 6) {
      return {
        economyListings: [],
        standardListings: listings,
        premiumListings: [],
      };
    }

    const { lowerBound, upperBound } = quartiles;
    const sortByReviews = (a, b) => b.reviewsCount - a.reviewsCount;

    const economyListings = listings
      .filter((l) => l.price.total <= lowerBound)
      .sort(sortByReviews);

    const standardListings = listings
      .filter((l) => l.price.total > lowerBound && l.price.total <= upperBound)
      .sort(sortByReviews);

    const premiumListings = listings
      .filter((l) => l.price.total > upperBound)
      .sort(sortByReviews);

    return { economyListings, standardListings, premiumListings };
  }, []);

  // Quartiles state
  const [quartiles, setQuartiles] = useState({ Q1: null, Q2: null, Q3: null });

  // 5) And keep your sort-by handler
  const handleSortChange = useCallback(
    (newSortBy) => {
      if (newSortBy) {
        // Good practice to check if newSortBy is a valid value
        setSortBy(newSortBy);
      }
      // That's it! No need to pick an itinerary or call setFlightResults here.
    },
    [setSortBy]
  );

  const handleStaySearch = useCallback(
    async (
      activeFilters,
      overrideDestination,
      prefetchedListings = null,
      overrideDepartureDate = null,
      overrideReturnDate = null,
      overrideCurrency = null,
      inExploreSession = false,
      silentPrefetch = false
    ) => {
      // use overrideDestination first

      const dest = overrideDestination ?? destination;
      const checkin = overrideDepartureDate ?? departureDate;
      const checkout = overrideReturnDate ?? returnDate;

      if (!dest || !checkin || !checkout) {
        logger.error("Please provide a destination and valid dates for stays.");
        return;
      }
      logger.time("Stays Search Duration");
      let airbnbSucceeded = false;
      const stayT0 = Date.now();
      let stayReportError = null;

      if (!silentPrefetch) {
        currentStaySearchIdRef.current += 1;
      }
      const myStaySearchId = currentStaySearchIdRef.current;

      if (!silentPrefetch) {
        if (searchType === "explore" || isExploring || inExploreSession) {
          setStayLoading(true);
        } else {
          setLoading(true);
        }
        setNoAirbnbFound(false); // Reset noAirbnbFound state
        setSubmittedDestination(dest);
        setSubmittedDepartureDate(departureDate);
        setSubmittedReturnDate(returnDate);
      }
      
      logger.log("🎯 handleStaySearch called with destination:", {
        override: overrideDestination,
        fallback: destination,
        silent: silentPrefetch
      });

      try {
        // Fetch Airbnb stays based on the destination
        const airbnbResults =
          prefetchedListings ??
          (await fetchAirbnbStays(dest, overrideCurrency ?? userCurrency));
        
        if (!silentPrefetch && currentStaySearchIdRef.current === myStaySearchId) {
          setAllFetchedListings(airbnbResults); // store the raw results
        }

        if (inExploreSession || isExploring || searchType === "explore" || silentPrefetch) {
          const destKey = `${dest.city},${dest.country_name || dest.country || ""}`;
          setPrefetchedExploreStays((prev) => ({...prev, [destKey]: airbnbResults}));
        }

        // Apply filters once
        const filteredResults = filterAirbnbResults(airbnbResults, {
          airbnbType,
          minReviewCount,
          minReviewScore,
          advancedFilters: activeFilters,
        });
        logger.log(
          `📦 Raw Airbnb Results: (${airbnbResults.length})\n` +
            `🧾 Filters: ` +
            `[superhostOnly=${activeFilters.superhostOnly ?? false}] ` +
            `[${airbnbType}] ` +
            `[minPrice=${activeFilters.minPrice ?? "not set"}] ` +
            `[maxPrice=${activeFilters.maxPrice ?? "not set"}] ` +
            (Object.keys(activeFilters)
              .filter((key) => key.startsWith("amenity_") && activeFilters[key])
              .map((key) => key.replace("amenity_", "")).length > 0
              ? `[amenities=${Object.keys(activeFilters)
                  .filter(
                    (key) => key.startsWith("amenity_") && activeFilters[key]
                  )
                  .map((key) => key.replace("amenity_", ""))
                  .join(",")}] `
              : "") +
            `\n📄 Pages fetched: ${Math.min(3, Math.ceil(airbnbResults.length / 20))} | ` +
            `Filtered: ${filteredResults.length}`
        );

        if (airbnbResults.length === 0) {
          logger.warn("No Airbnb stays found.");
          setNoAirbnbFound(true);
        } else {
          // Process valid Airbnb results
          const nights = Math.ceil(
            (returnDate - departureDate) / (1000 * 60 * 60 * 24)
          );

          // Normalize prices and filter valid results
          const filteredResults = filterAirbnbResults(airbnbResults, {
            airbnbType,
            minReviewCount,
            minReviewScore,
            advancedFilters: activeFilters,
          });
          logger.log(
            `Filtered results (${filteredResults.length}) within $${activeFilters.minPrice} - $${activeFilters.maxPrice}:`,
            filteredResults.map((r) => ({
              name: r.name,
              price: r.price.total,
              rating: r.rating,
              reviews: r.reviewsCount,
            }))
          );

          const validAirbnbResults = normalizeListingPrices(
            filteredResults,
            nights
          );

          if (silentPrefetch) {
             if (validAirbnbResults.length > 0) {
                const prices = validAirbnbResults.map((r) => r.price.total).filter(Boolean);
                let median;
                if (validAirbnbResults.length < 9) {
                   const mid = Math.floor(prices.length / 2);
                   median = prices.length % 2 !== 0 ? prices[mid] : (prices[mid - 1] + prices[mid]) / 2;
                } else {
                   const calculatedQuartiles = calculateQuartiles(prices);
                   median = calculatedQuartiles.Q2;
                }
                const destKey = `${dest.city},${dest.country_name || dest.country || ""}`;
                setPrefetchedExploreMedians((prev) => ({...prev, [destKey]: median}));
             }
          } else if (currentStaySearchIdRef.current === myStaySearchId) {
            // Handle no valid Airbnb results
            if (validAirbnbResults.length === 0) {
              setNoAirbnbFound(true);
              setSearchInitiated(true);
				setNoFlightsFound(false); // ADD THIS so it still shows Results with “no results found”
            } else {
              setNoAirbnbFound(false);
              setSearchInitiated(true);
				setNoFlightsFound(false);
              airbnbSucceeded = true;

              if (validAirbnbResults.length < 9) {
                setEconomyListings([]);
                setStandardListings(validAirbnbResults);
                setPremiumListings([]);
                setSelectedStayCategory("standard");

                const prices = validAirbnbResults
                  .map((r) => r.price.total)
                  .filter(Boolean);
                const mid = Math.floor(prices.length / 2);
                const median =
                  prices.length % 2 !== 0
                    ? prices[mid]
                    : (prices[mid - 1] + prices[mid]) / 2;
                setQuartiles({ Q1: null, Q2: median, Q3: null });
                if (inExploreSession || isExploring || searchType === "explore") {
                  const destKey = `${dest.city},${dest.country_name || dest.country || ""}`;
                  setPrefetchedExploreMedians((prev) => ({...prev, [destKey]: median}));
                }
              } else {
                // normal quartile calculation
                let calculatedQuartiles;
                if (validAirbnbResults.length === 1) {
                  const price = validAirbnbResults[0].price.total;
                  calculatedQuartiles = { Q1: price, Q2: price, Q3: price };
                } else if (validAirbnbResults.length === 2) {
                  calculatedQuartiles = {
                    Q1: validAirbnbResults[0].price.total,
                    Q2:
                      (validAirbnbResults[0].price.total +
                        validAirbnbResults[1].price.total) /
                      2,
                    Q3: validAirbnbResults[1].price.total,
                  };
                } else {
                  const totalPrices = validAirbnbResults.map(
                    (result) => result.price.total
                  );
                  calculatedQuartiles = calculateQuartiles(totalPrices);
                }

                setQuartiles(calculatedQuartiles);
                if (inExploreSession || isExploring || searchType === "explore") {
                  const destKey = `${dest.city},${dest.country_name || dest.country || ""}`;
                  setPrefetchedExploreMedians((prev) => ({...prev, [destKey]: calculatedQuartiles.Q2}));
                }

                const { economyListings, standardListings, premiumListings } =
                  classifyAndSortListingsByPrice(
                    validAirbnbResults,
                    calculatedQuartiles
                  );

                setEconomyListings(economyListings);
                setStandardListings(standardListings);
                setPremiumListings(premiumListings);
              }
            }
          }
        }
      } catch (error) {
        logger.error("Error fetching stays:", error);
        stayReportError = error?.message;
      } finally {
        if (!silentPrefetch && currentStaySearchIdRef.current === myStaySearchId) {
          if (searchType === "explore" || isExploring || inExploreSession) {
            setStayLoading(false);
          } else {
            setLoading(false);
          }
        }
        logger.timeEnd("Stays Search Duration");
        reportTiming(
          "airbnb",
          Date.now() - stayT0,
          airbnbSucceeded,
          { dest: dest?.city || dest?.city_id || null },
          stayReportError
        );
      }
      return airbnbSucceeded;
    },
    [
      destination,
      departureDate,
      returnDate,
      fetchAirbnbStays,
      airbnbType,
      normalizeListingPrices,
      calculateQuartiles,
      classifyAndSortListingsByPrice,
      minReviewCount,
      minReviewScore,
      isExploring,
      userCurrency,
      searchType,
    ]
  );

  const handleExploreSearch = async ({
    origin,
    departureDate,
    travelTime,
    transportMode,
    preferences,
    description,
    flightType,
    returnDate,
    adults,
    children,
    destinationVibe: vibeOverride,
  }) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSearchType(""); // clear out the old searchType briefly
    setSearchInitiated(false);
    setIsExploring(false);
    setDestination(null);
    setDestinationInput("");
    setExploreResults([]);
    setPrefetchedExploreStays({});
    setPrefetchedExploreFlights({});
    setSelectedExploreCity(null);
    setExploreFlightPrefs(null);
    setToFly(transportMode === "flight");
    setFlightResults([]);
    setNoFlightsFound(false);
    setFlightLoading(false);

    setEconomyListings([]);
    setStandardListings([]);
    setPremiumListings([]);
    setNoAirbnbFound(false);
    setStayLoading(false);
    setSearchType("explore");
    setSearchInitiated(true);
				setNoFlightsFound(false);
    setExploreLoading(true);

    const exploreT0 = Date.now();
    let exploreSuccess = false;
    let exploreErrMsg = null;
    try {
      logger.time("Explore API Request");
      const response = await fetch(
        "https://flybnb.up.railway.app/api/explore-suggestions",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(await getAuthHeader()) },
          body: JSON.stringify({
            originCity: {
              city: origin?.city,
              country: origin?.country_name,
            },
            departureDate,
            maxTravelTime: travelTime,
            transportMode,
            preferences,
            description,
            destinationVibe: vibeOverride ?? destinationVibe,
          }),
        }
      );
      logger.timeEnd("Explore API Request");

      const data = await response.json();

      if (data?.suggestions) {
        exploreSuccess = (data.suggestions.length || 0) > 0;
        const mappedSuggestions = data.suggestions.map((s) => ({
          ...s,
          transportMode: transportMode,
        }));
        setExploreResults(mappedSuggestions);

        // SILENT PREFETCH TRIGGER
        (async () => {
          for (const cityObj of mappedSuggestions) {
            const dest = { city: cityObj.city, country_name: cityObj.country, country: cityObj.country };
            const searchTypeToUse = (tofly || exploreFlightPrefs) ? "flightsAndStays" : "staysOnly";
            try {
              await handleSearch(
                searchTypeToUse,
                advancedFilters,
                dest,
                null,
                null,
                userCurrency,
                true, // inExploreSession
                null, // prefetchedFlightsData
                true  // silentPrefetch
              );
            } catch (e) {
              logger.error("Silent prefetch error:", e);
            }
          }
        })();
      } else {
        setExploreResults([]);
        logger.error(
          "Explore AI error:",
          data?.error || "No suggestions returned"
        );
      }
    } catch (err) {
      logger.timeEnd("Explore API Request");
      logger.error("Explore request failed:", err);
      exploreErrMsg = err?.message;
    } finally {
      setExploreLoading(false);
      reportTiming(
        "explore",
        Date.now() - exploreT0,
        exploreSuccess,
        { origin: origin?.city || null },
        exploreErrMsg
      );
    }
  };

  const logSearchData = useCallback(async () => {
    // Get user_id if logged in, fall back to a persistent anonymous session ID
    const { data: { user: authUser } } = await supabase.auth.getUser();
    let sessionId = authUser?.id || null;
    if (!sessionId) {
      sessionId = localStorage.getItem("flybnb_session_id");
      if (!sessionId) {
        sessionId = crypto.randomUUID();
        localStorage.setItem("flybnb_session_id", sessionId);
      }
    }

    const { data, error } = await supabase.from("search_logs").insert([
      {
        origin: origin?.city || null,
        destination: destination?.city || null,
        departure_date: departureDate || null,
        return_date: returnDate || null,
        search_type: searchType,
        flight_type: flightType,
        adults: adults,
        children: children,
        stay_type: airbnbType,
        min_review_score: minReviewScore,
        min_review_count: minReviewCount,
        session: sessionId,
        logged_at: new Date().toISOString(),
      },
    ]).select("id").single();

    if (error) {
      logger.error("Error logging search data:", error.message);
      return null;
    }
    logger.log("Search data logged successfully");
    return data?.id ?? null;
  }, [
    origin?.city,
    destination?.city,
    departureDate,
    returnDate,
    searchType,
    flightType,
    adults,
    children,
    airbnbType,
    minReviewScore,
    minReviewCount,
  ]);

  const handleFlightError = useCallback((error) => {
    const status = error.response?.status;
    if (status === 502) {
      logger.error("Bad Gateway: The server is temporarily unavailable.");
    } else if (status === 404) {
      logger.warn("No flights found.");
    } else {
      logger.error("An unexpected error occurred:", error);
    }
    if (status === 429 || status === 403 || status === 402) {
      sendApiAlert({
        api: "flights",
        statusCode: status,
        message: error.response?.data?.message || error.message,
        endpoint: "google-flights2.p.rapidapi.com",
      });
    }
    setNoFlightsFound(true);
  }, []);

  const handleSearch = useCallback(
    async (
      currentSearchType,
      incomingAdvancedFilters = {},
      overrideDestination = null,
      prefetchedListings = null,
      overrideDestinationCityId = null,
      currency = "USD", 
      inExploreSession = false, 
      prefetchedFlightsData = null, 
      silentPrefetch = false // ✅ Added for prefetching
    ) => {
      // ✅ MOBILE FIX: Dismiss Keyboard & Scroll to Top immediately
      if (document.activeElement instanceof HTMLElement && !silentPrefetch) {
        document.activeElement.blur(); // Closes mobile keyboard
      }
      if (!inExploreSession && !silentPrefetch) {
        // Instant (not smooth) — a smooth animation gets interrupted by the
        // hero band collapsing to display:none, leaving the page scrolled down.
        window.scrollTo({ top: 0, behavior: "auto" });
      }

      if (!silentPrefetch) {
        currentSearchIdRef.current += 1;
      }
      const mySearchId = currentSearchIdRef.current;

      if (!silentPrefetch) {
        setSearchType(currentSearchType);
        setFlightRetryAttempt(0); 
        setUserCurrency(currency);
      }

      // Feature gating: check daily usage limits for free users
      const isExploreSearch = currentSearchType === "explore";
      const featureKey = isExploreSearch ? "explore" : "search";
      const featureLimit = isExploreSearch ? FREE_LIMITS.explore : FREE_LIMITS.search;
      const featureLabel = isExploreSearch ? "Explore" : "Search";
      if (!canUseFeature(featureKey, featureLimit, isPro)) {
        if (!silentPrefetch) setLimitDialog({ feature: featureLabel, limit: featureLimit });
        return;
      }
      if (!silentPrefetch) incrementUsage(featureKey);

      if (currentSearchType === "staysOnly") {
        if (!inExploreSession && !silentPrefetch) setLoading(true);
        await handleStaySearch(
          incomingAdvancedFilters,
          overrideDestination,
          prefetchedListings,
          departureDate,
          returnDate,
          currency,
          inExploreSession,
          silentPrefetch
        );
        if (!inExploreSession && !silentPrefetch && currentSearchIdRef.current === mySearchId) setLoading(false);
        if (!silentPrefetch && currentSearchIdRef.current === mySearchId) setFlightResults([]); 
        return;
      }

      if (currentSearchType !== "explore" && !inExploreSession && !silentPrefetch && currentSearchIdRef.current === mySearchId) {
        setLoading(true);
      }
      if (!inExploreSession && !silentPrefetch && currentSearchIdRef.current === mySearchId) {
        setFlightResults([]);
      }
      
      if (!silentPrefetch && currentSearchIdRef.current === mySearchId) {
        setSortBy("best");
        setSearchInitiated(true);
				setNoFlightsFound(false);
      }

      // 1) pick the right origin/dest
      const originForThisSearch = origin;
      let destForThisSearch = overrideDestination || destination;
      if (currentSearchType === "explore" && destForThisSearch?.city) {
        const resolved = resolveCityFromIATA(
          destForThisSearch.city,
          destForThisSearch.country
        );
        if (resolved) destForThisSearch = resolved;
      }
      const departureCity = originForThisSearch?.city;
      const arrivalCity = destForThisSearch?.city;

      // 2) validation
      if (
        (currentSearchType !== "staysOnly" && !originForThisSearch) ||
        !destForThisSearch ||
        !departureDate ||
        (flightType === "roundtrip" && !returnDate)
      ) {
        logger.error("Missing origin/destination or dates");
        setNoFlightsFound(true);
        setLoading(false);
        return;
      }

      // 3) fetch city IDs — skip if staysOnly
      let originCityId = null;
      let destinationCityId = null;

      if (currentSearchType !== "staysOnly") {
        [originCityId, destinationCityId] = await Promise.all([
          getCityId(originForThisSearch),
          overrideDestinationCityId
            ? Promise.resolve(overrideDestinationCityId) // ✅ Use direct ID
            : getCityId(destForThisSearch),
        ]);
        logger.log("🌍 Origin City ID:", originCityId);
        logger.log("🌍 Destination City ID:", destinationCityId);
        logger.log("🎯 Destination object passed in:", destForThisSearch);

        if (!originCityId || !destinationCityId) {
          logger.error("Failed to resolve city IDs — skipping flights, stays will still run");
          if (!silentPrefetch && currentSearchIdRef.current === mySearchId) setNoFlightsFound(true);
        }

      }
      if (!silentPrefetch && currentSearchIdRef.current === mySearchId) {
        setSubmittedOrigin(originForThisSearch);
        setSubmittedDestination(destForThisSearch);
      }
      logger.log("🌍 Search will use currency:", currency);
      // The flight API intermittently returns empty results (often on a slow call) —
      // retry the same city-ID search a few times before falling back to IATA codes.
      const MAX_CITYID_RETRIES = 2;
      const RETRY_DELAY_MS = 1200;
      let triedIATAFallback = false;

      // 4) helper: fetch & normalize outbound itineraries
      const fetchOutbound = async (overrideDepartureId, overrideArrivalId, attempt = 0) => {
        const departureId = overrideDepartureId || originCityId;
        const arrivalId = overrideArrivalId || destinationCityId;
        logger.time("✈️ searchFlights outbound");
        const resp = await axios.request({
          method: "GET",
          url: "https://google-flights2.p.rapidapi.com/api/v1/searchFlights",
          params: {
            departure_id: departureId,
            arrival_id: arrivalId,
            outbound_date: departureDate.toLocaleDateString("en-CA"),
            ...(flightType === "roundtrip" && returnDate
              ? { return_date: returnDate.toLocaleDateString("en-CA") }
              : {}),
            travel_class: "ECONOMY",
            adults: String(adults),
            children: String(children),
            currency: currency,
            language_code: "en-US",
            country_code: "US",
          },
          headers: {
            "x-rapidapi-key": process.env.REACT_APP_RAPIDAPI_KEY,
            "x-rapidapi-host": "google-flights2.p.rapidapi.com",
          },
          timeout: 30000,
        });
        logger.timeEnd("✈️ searchFlights outbound");

        const topFlights = resp.data?.data?.itineraries?.topFlights || [];
        const otherFlights = resp.data?.data?.itineraries?.otherFlights || [];
        const priceHistoryRaw = resp.data?.data?.priceHistory ?? null;
        if (priceHistoryRaw) setFlightPriceHistory(priceHistoryRaw);
        const allFlights = [...topFlights, ...otherFlights];

        logger.log("📦 Outbound topFlights:", topFlights.length, "otherFlights:", otherFlights.length);

        if (allFlights.length === 0) {
          const usingIATA = Boolean(overrideDepartureId);

          // The API is flaky and sometimes returns empty for a valid route —
          // retry the same (correct) city-ID search before doing anything else.
          if (!usingIATA && attempt < MAX_CITYID_RETRIES) {
            logger.warn(`No flights on attempt ${attempt + 1} — retrying same city-ID search...`);
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
            return await fetchOutbound(overrideDepartureId, overrideArrivalId, attempt + 1);
          }

          // City-ID search exhausted — fall back to IATA codes once as a last resort.
          if (!usingIATA && !triedIATAFallback) {
            triedIATAFallback = true;
            const originIATA = originForThisSearch?.airports?.[0]?.iata;
            const destIATA = destForThisSearch?.airports?.[0]?.iata;
            if (originIATA && destIATA) {
              logger.warn(`City-ID search exhausted — falling back to IATA codes: ${originIATA} → ${destIATA}`);
              return await fetchOutbound(originIATA, destIATA, 0);
            }
          }

          setNoFlightsFound(true);
          return { flights: [], bestRaw: null };
        }

        // Pick best from topFlights[0], fallback to otherFlights[0]
        const bestFlight = topFlights[0] || otherFlights[0];

        const firstFlight = bestFlight;
        if (!firstFlight?.flights?.length) {
          return { flights: [], bestRaw: null };
        }

        const primaryLeg = firstFlight.flights[0] || {};

        return {
          bestRaw: bestFlight,
          flights: allFlights.map((flight, index) => {
            const primaryLeg = flight.flights[0] || {};
            const layovers = flight.layovers || [];

            return {
              nextToken: flight.next_token,
              bookingToken: flight.booking_token,
              price: typeof flight.price === "number" ? flight.price : NaN,
              priceLabel:
                typeof flight.price === "number"
                  ? `$${flight.price}`
                  : "Unavailable",
              durationRaw: flight.duration?.raw,
              durationText: flight.duration?.text,
              departureTime: flight.departure_time,
              arrivalTime: flight.arrival_time,
              stops: flight.stops,
              bags: flight.bags,
              carbonEmissions: flight.carbon_emissions ?? null,
              legroom: flight.flights?.[0]?.legroom ?? null,
              layovers: layovers.map((l) => ({
                city: l.city,
                code: l.airport_code,
                duration: l.duration,
                durationLabel: l.duration_label,
              })),
              airlineLogo: primaryLeg.airline_logo || null,
              id: `itinerary-${index}-${flight.booking_token || Math.random()}`,
              fromCity: departureCity,
              toCity: arrivalCity,
              legs: flight.flights.map((leg) => {
                const matchedArrivalLayover = layovers.find(
                  (l) => l.airport_code === leg.arrival_airport.airport_code
                );
                const matchedDepartureLayover = layovers.find(
                  (l) => l.airport_code === leg.departure_airport.airport_code
                );

                return {
                  flightNumber: leg.flight_number,
                  aircraft: leg.aircraft,
                  airline: leg.airline,
                  airlineLogo: leg.airline_logo,
                  departure: {
                    code: leg.departure_airport.airport_code,
                    city:
                      matchedDepartureLayover?.city ||
                      leg.departure_airport.airport_name,
                    airportName: leg.departure_airport.airport_name,
                    time: leg.departure_airport.time,
                  },
                  arrival: {
                    code: leg.arrival_airport.airport_code,
                    city:
                      matchedArrivalLayover?.city ||
                      leg.arrival_airport.airport_name,
                    airportName: leg.arrival_airport.airport_name,
                    time: leg.arrival_airport.time,
                  },
                  durationRaw: leg.duration.raw,
                  durationText: leg.duration.text,
                  legroom: leg.legroom,
                  extensions: leg.extensions,
                };
              }),
            };
          }), // end map
        }; // end return object
      };

      // ─── SerpAPI fallback ──────────────────────────────────────────────────
      const fetchOutboundSerpAPI = async () => {
        const serpParams = {
          engine: "google_flights",
          departure_id: originCityId,
          arrival_id: destinationCityId,
          outbound_date: departureDate.toLocaleDateString("en-CA"),
          ...(flightType === "roundtrip" && returnDate
            ? { return_date: returnDate.toLocaleDateString("en-CA") }
            : { type: "2" }),
          adults: String(adults),
          children: String(children),
          currency: currency,
          hl: "en",
          api_key: process.env.REACT_APP_SERPAPI_KEY,
        };

        const resp = await axios.get("/api/serpapi-proxy", {
          params: serpParams,
          timeout: 30000,
        });

        const bestFlights = resp.data?.best_flights || [];
        const otherFlights = resp.data?.other_flights || [];
        const allFlights = [...bestFlights, ...otherFlights];

        logger.log("🔄 SerpAPI outbound — best:", bestFlights.length, "other:", otherFlights.length);

        if (allFlights.length === 0) {
          return { flights: [], bestRaw: null, usingSerpAPI: true };
        }

        const mapSerpFlight = (flight) => {
          const legs = flight.flights || [];
          const firstLeg = legs[0] || {};
          const lastLeg = legs[legs.length - 1] || {};
          const layovers = flight.layovers || [];

          return {
            nextToken: flight.departure_token || null,
            bookingToken: flight.booking_token || null,
            price: typeof flight.price === "number" ? flight.price : NaN,
            priceLabel: typeof flight.price === "number" ? `$${flight.price}` : "Unavailable",
            durationRaw: flight.total_duration ?? null,
            durationText: formatSerpDuration(flight.total_duration),
            departureTime: firstLeg.departure_airport?.time ?? null,
            arrivalTime: lastLeg.arrival_airport?.time ?? null,
            stops: layovers.length,
            bags: null,
            carbonEmissions: flight.carbon_emissions ?? null,
            legroom: firstLeg.legroom ?? null,
            layovers: layovers.map((l) => ({
              city: l.name || l.city,
              code: l.airport_code || l.id,
              duration: l.duration,
              durationLabel: l.duration_label || formatSerpDuration(l.duration),
            })),
            airlineLogo: firstLeg.airline_logo || null,
            fromCity: departureCity,
            toCity: arrivalCity,
            legs: legs.map((leg) => ({
              flightNumber: leg.flight_number,
              aircraft: leg.airplane,
              airline: leg.airline,
              airlineLogo: leg.airline_logo,
              departure: {
                code: leg.departure_airport?.airport_code || leg.departure_airport?.id,
                city: leg.departure_airport?.name,
                airportName: leg.departure_airport?.name,
                time: leg.departure_airport?.time,
              },
              arrival: {
                code: leg.arrival_airport?.airport_code || leg.arrival_airport?.id,
                city: leg.arrival_airport?.name,
                airportName: leg.arrival_airport?.name,
                time: leg.arrival_airport?.time,
              },
              durationRaw: leg.duration,
              durationText: formatSerpDuration(leg.duration),
              legroom: leg.legroom,
              extensions: leg.extensions,
            })),
          };
        };

        const mappedFlights = allFlights.map(mapSerpFlight);
        // Tag best/cheapest/fastest directly — SerpAPI best_flights[0] is already the recommended option
        if (mappedFlights.length > 0) mappedFlights[0].isBest = true;

        return {
          bestRaw: null, // isBest already tagged above; skip the departure_time match in flightPromise
          flights: mappedFlights,
          usingSerpAPI: true,
        };
      };

      try {
        if (!silentPrefetch && currentSearchIdRef.current === mySearchId) setFlightLoading(true);
        const searchLogId = !silentPrefetch ? await logSearchData() : null;
        let flightsFound = null;
        let airbnbFound = null;

        const flightPromise = (originCityId && destinationCityId) ? (async () => {
          if (prefetchedFlightsData && prefetchedFlightsData.length > 0) {
            if (!silentPrefetch && currentSearchIdRef.current === mySearchId) {
              setFlightResults(prefetchedFlightsData);
              setFlightLoading(false);
              setLoading(false);
            }
            flightsFound = true;
            return;
          }

          const flightT0 = Date.now();
          let result;
          let usingSerpAPI = false;
          try {
            result = await fetchOutbound();
            if ((result?.flights ?? []).length === 0 && SERPAPI_FALLBACK_ENABLED) {
              logger.log("🔄 Primary returned 0 flights — trying SerpAPI fallback...");
              result = await fetchOutboundSerpAPI();
              usingSerpAPI = result?.usingSerpAPI === true;
              if ((result?.flights ?? []).length > 0) setNoFlightsFound(false);
            }
          } catch (err) {
            reportTiming("flight", Date.now() - flightT0, false, {
              origin: originForThisSearch?.city,
              dest: destForThisSearch?.city,
            }, err?.message);
            throw err;
          }
          const flights = result?.flights ?? [];
          reportTiming("flight", Date.now() - flightT0, flights.length > 0, {
            origin: originForThisSearch?.city,
            dest: destForThisSearch?.city,
          });
          const bestRaw = result?.bestRaw ?? null;
          if (flights.length === 0) { 
             if (!silentPrefetch && currentSearchIdRef.current === mySearchId) setNoFlightsFound(true); 
             flightsFound = false; 
             return; 
          }

          // Tag best, cheapest, and fastest
          if (flights.length > 0) {
            if (bestRaw) {
              const bestIndex = flights.findIndex(
                (f) =>
                  f.departureTime === bestRaw.departure_time &&
                  f.arrivalTime === bestRaw.arrival_time
              );
              // Always guarantee isBest is set — fall back to [0] if match fails
              flights[bestIndex !== -1 ? bestIndex : 0].isBest = true;
              if (bestIndex === -1) logger.warn("⚠️ isBest match failed — defaulting to flights[0]");
            }

            let cheapestIndex = 0;
            let fastestIndex = 0;

            for (let i = 1; i < flights.length; i++) {
              if (flights[i].price < flights[cheapestIndex].price) {
                cheapestIndex = i;
              }
              if (flights[i].durationRaw < flights[fastestIndex].durationRaw) {
                fastestIndex = i;
              }
            }

            flights[cheapestIndex].isCheapest = true;
            flights[fastestIndex].isFastest = true;
          }

          // roundtrip enhancement
          if (flightType === "roundtrip" && flights.length) {
            console.log("🔁 Roundtrip enrichment — nextTokens:", flights.map(f => ({ isBest: f.isBest, isCheapest: f.isCheapest, isFastest: f.isFastest, nextToken: f.nextToken ?? "MISSING" })));
            const enriched = await Promise.all(
              flights.map(async (it) => {
                if (!it.nextToken) return it;

                // Only enrich if it's one of the 3 tagged flights
                if (!it.isBest && !it.isCheapest && !it.isFastest) return it;

                // 🚨 NEW: guard to avoid duplicate enrichment
                if (it.returnLegs && it.returnLegs.length > 0) {
                  logger.log(
                    "🔁 Skipping re-enrich, returnLegs already present"
                  );
                  return it;
                }

                const RETRY_DELAY_MS = 500;
                let lastErr;

                if (usingSerpAPI) {
                  // ── SerpAPI return enrichment via departure_token ──────
                  const SERP_MAX_RETRIES = 3;
                  for (let attempt = 1; attempt <= SERP_MAX_RETRIES; attempt++) {
                    try {
                      logger.log(`🔁 Searching for return flight (SerpAPI attempt ${attempt}/${SERP_MAX_RETRIES})`);
                      const serpRet = await axios.get("/api/serpapi-proxy", {
                        params: {
                          engine: "google_flights",
                          departure_id: originCityId,
                          arrival_id: destinationCityId,
                          outbound_date: departureDate.toLocaleDateString("en-CA"),
                          return_date: returnDate.toLocaleDateString("en-CA"),
                          departure_token: it.nextToken,
                          currency: currency,
                          hl: "en",
                          api_key: process.env.REACT_APP_SERPAPI_KEY,
                        },
                        timeout: 30000,
                      });

                      const serpRetBest = serpRet.data?.best_flights || [];
                      const serpRetOther = serpRet.data?.other_flights || [];
                      const allRetFlights = [...serpRetBest, ...serpRetOther];

                      if (!allRetFlights.length) {
                        logger.warn(`⚠️ SerpAPI return attempt ${attempt}: empty, retrying...`);
                        if (attempt < SERP_MAX_RETRIES) {
                          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
                          continue;
                        }
                        break;
                      }

                      logger.log("📦 SerpAPI return flights received:", allRetFlights.length);
                      let chosenRet;
                      if (it.isCheapest) {
                        chosenRet = allRetFlights.reduce((best, r) =>
                          (r.price ?? Infinity) < (best.price ?? Infinity) ? r : best, allRetFlights[0]);
                      } else if (it.isFastest) {
                        chosenRet = allRetFlights.reduce((best, r) =>
                          (r.total_duration ?? Infinity) < (best.total_duration ?? Infinity) ? r : best, allRetFlights[0]);
                      } else {
                        chosenRet = serpRetBest[0] || allRetFlights[0];
                      }
                      const retLegs = (chosenRet?.flights || []).map((leg) => ({
                        flightNumber: leg.flight_number,
                        aircraft: leg.airplane || leg.aircraft,
                        airline: leg.airline,
                        airlineLogo: leg.airline_logo,
                        departure: {
                          code: leg.departure_airport?.id || leg.departure_airport?.airport_code,
                          city: leg.departure_airport?.name,
                          airportName: leg.departure_airport?.name,
                          time: leg.departure_airport?.time,
                        },
                        arrival: {
                          code: leg.arrival_airport?.id || leg.arrival_airport?.airport_code,
                          city: leg.arrival_airport?.name,
                          airportName: leg.arrival_airport?.name,
                          time: leg.arrival_airport?.time,
                        },
                        durationRaw: leg.duration?.raw ?? leg.duration,
                        durationText: leg.duration?.text ?? formatSerpDuration(leg.duration),
                        legroom: leg.legroom,
                        extensions: leg.extensions,
                      }));
                      return {
                        ...it,
                        id: `${it.id}-ret-${chosenRet?.booking_token || Math.random()}`,
                        returnLegs: retLegs,
                        returnOverallDurationText: formatSerpDuration(chosenRet?.total_duration),
                        returnOverallDurationRaw: chosenRet?.total_duration,
                        bookingToken: chosenRet?.booking_token || it.bookingToken,
                      };
                    } catch (err) {
                      lastErr = err;
                      logger.warn(`❌ SerpAPI return attempt ${attempt} failed:`, err?.message);
                      if (attempt < SERP_MAX_RETRIES) await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
                    }
                  }
                } else {
                  // ── RapidAPI return: use getNextFlights with next_token to get returns paired with this outbound ──
                  // next_token encodes the selected outbound; getNextFlights returns matched return options
                  // with a roundtrip booking_token per itinerary.
                  try {
                    const timerLabel = `🔁 getNextFlights (${it.isBest ? "best" : it.isCheapest ? "cheapest" : "fastest"})`;
                    logger.log(`🔁 Fetching return options via getNextFlights for ${it.isBest ? "best" : it.isCheapest ? "cheapest" : "fastest"}`);
                    logger.time(timerLabel);
                    const ret = await axios.request({
                      method: "GET",
                      url: "https://google-flights2.p.rapidapi.com/api/v1/getNextFlights",
                      params: {
                        next_token: it.nextToken,
                        show_hidden: "1",
                        currency: currency,
                        language_code: "en-US",
                        country_code: "US",
                      },
                      headers: {
                        "x-rapidapi-key": process.env.REACT_APP_RAPIDAPI_KEY,
                        "x-rapidapi-host": "google-flights2.p.rapidapi.com",
                      },
                      timeout: 30000,
                    });
                    logger.timeEnd(timerLabel);

                    const retData = ret.data?.data?.itineraries;
                    const allRetFlights = [
                      ...(retData?.topFlights || []),
                      ...(retData?.otherFlights || []),
                    ];

                    if (!allRetFlights.length) {
                      logger.warn(`⚠️ getNextFlights returned no return flights`);
                    } else {
                      logger.log("📦 Return flights received via getNextFlights:", allRetFlights.length);
                      const retIt = retData?.topFlights?.[0] || retData?.otherFlights?.[0] || null;
                      let chosenRet;
                      if (it.isCheapest) {
                        chosenRet = allRetFlights.reduce((best, r) =>
                          (r.price ?? Infinity) < (best.price ?? Infinity) ? r : best, retIt || {});
                      } else if (it.isFastest) {
                        chosenRet = allRetFlights.reduce((best, r) =>
                          (r.duration?.raw ?? Infinity) < (best.duration?.raw ?? Infinity) ? r : best, retIt || {});
                      } else {
                        chosenRet = retIt || {};
                      }
                      return {
                        ...it,
                        returnLegs: normalizeReturnLegs(chosenRet?.flights),
                        returnOverallDurationText: chosenRet?.duration?.text,
                        returnOverallDurationRaw: chosenRet?.duration?.raw,
                        bookingToken: chosenRet?.booking_token || it.bookingToken,
                      };
                    }
                  } catch (err) {
                    lastErr = err;
                    logger.warn(`❌ getNextFlights return flight failed:`, err?.message);
                  }
                }
                logger.warn("❌ All return flight candidates exhausted", lastErr);
                return { ...it, returnLegs: it.returnLegs ?? [] };
              })
            );
            // Re-tag fastest using combined outbound + return duration.
            // Unenriched flights (no returnLegs) get Infinity so they can't win.
            const combinedDuration = (f) =>
              (f.returnLegs?.length ?? 0) > 0
                ? (f.durationRaw || 0) + (f.returnOverallDurationRaw || 0)
                : Infinity;

            // Clear old fastest tag (was set before return data was known)
            enriched.forEach((f) => { f.isFastest = false; });
            let fastestIdx = 0;
            for (let i = 1; i < enriched.length; i++) {
              if (combinedDuration(enriched[i]) < combinedDuration(enriched[fastestIdx])) {
                fastestIdx = i;
              }
            }
            enriched[fastestIdx].isFastest = true;

            logger.log("✅ Post-enrichment:", enriched.map(f => ({
              isBest: f.isBest, isCheapest: f.isCheapest, isFastest: f.isFastest,
              returnLegs: f.returnLegs?.length ?? "MISSING",
              nextToken: f.nextToken ? "✓" : "null",
            })));
            if (!silentPrefetch && currentSearchIdRef.current === mySearchId) setFlightResults(enriched);
            if ((inExploreSession || silentPrefetch) && destForThisSearch?.city) {
              const destKey = `${destForThisSearch.city},${destForThisSearch.country_name || destForThisSearch.country || ""}`;
              setPrefetchedExploreFlights((prev) => ({ ...prev, [destKey]: enriched }));
            }
            flightsFound = true;
            if (!silentPrefetch && currentSearchIdRef.current === mySearchId) setLoading(false);
          } else {
            if (!silentPrefetch && currentSearchIdRef.current === mySearchId) setFlightResults(flights);
            if ((inExploreSession || silentPrefetch) && destForThisSearch?.city) {
              const destKey = `${destForThisSearch.city},${destForThisSearch.country_name || destForThisSearch.country || ""}`;
              setPrefetchedExploreFlights((prev) => ({ ...prev, [destKey]: flights }));
            }
            flightsFound = true;
            if (!silentPrefetch && currentSearchIdRef.current === mySearchId) setLoading(false);
          }
        })() : Promise.resolve();

        const stayPromise =
          currentSearchType !== "explore"
            ? handleStaySearch(
                incomingAdvancedFilters,
                overrideDestination,
                prefetchedListings,
                departureDate,
                returnDate,
                currency,
                inExploreSession,
                silentPrefetch
              )
            : Promise.resolve();

        const [, staySettled] = await Promise.allSettled([flightPromise, stayPromise]);
        if (currentSearchType !== "explore") {
          airbnbFound = staySettled?.status === "fulfilled" ? (staySettled.value ?? false) : false;
        }
        if (searchLogId) {
          supabase.from("search_logs")
            .update({ flights_found: flightsFound, airbnb_found: airbnbFound })
            .eq("id", searchLogId).then(() => {});
        }
      } catch (err) {
        if (!silentPrefetch && currentSearchIdRef.current === mySearchId) handleFlightError(err);
      } finally {
        if (!silentPrefetch && currentSearchIdRef.current === mySearchId) {
          setLoading(false);
          setFlightLoading(false);
        }
      }
    },
    [
      origin,
      destination,
      departureDate,
      returnDate,
      adults,
      children,
      flightType,
      getCityId,
      logSearchData,
      handleStaySearch,
      handleFlightError,
      isExploring,
      isPro,
      setUserCurrency,
    ]
  );

  // --- Shareable / saveable search wiring ---

  const currentSearchParams = useCallback(
    () =>
      buildSearchParams({
        origin,
        destination,
        departureDate,
        returnDate,
        adults,
        children,
        flightType,
        airbnbType,
        searchType,
        minReviewCount,
        minReviewScore,
        userCurrency,
        advancedFilters,
      }),
    [
      origin,
      destination,
      departureDate,
      returnDate,
      adults,
      children,
      flightType,
      airbnbType,
      searchType,
      minReviewCount,
      minReviewScore,
      userCurrency,
      advancedFilters,
    ]
  );

  const pushSearchToUrl = useCallback(() => {
    try {
      const qs = paramsToQueryString(currentSearchParams());
      const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState(null, "", url);
    } catch (e) {
      logger.warn?.("pushSearchToUrl failed", e);
    }
  }, [currentSearchParams]);

  const applySearchFromParams = useCallback(
    (parsed) => {
      if (!parsed) return;
      const originCity = parsed.originKey
        ? findCityByKey(citiesData, parsed.originKey)
        : null;
      const destCity = parsed.destinationKey
        ? findCityByKey(citiesData, parsed.destinationKey)
        : null;
      if (originCity) {
        setOrigin(originCity);
        setOriginInput(
          `${originCity.city}, ${originCity.country_name || ""}`
            .trim()
            .replace(/,\s*$/, "")
        );
      }
      if (destCity) {
        setDestination(destCity);
        setDestinationInput(
          `${destCity.city}, ${destCity.country_name || ""}`
            .trim()
            .replace(/,\s*$/, "")
        );
      }
      if (parsed.departureDate) setDepartureDate(parsed.departureDate);
      if (parsed.returnDate) setReturnDate(parsed.returnDate);
      if (parsed.adults != null) setAdults(parsed.adults);
      if (parsed.children != null) setChildren(parsed.children);
      if (parsed.flightType) setFlightType(parsed.flightType);
      if (parsed.airbnbType) setAirbnbType(parsed.airbnbType);
      if (parsed.searchType) setSearchType(parsed.searchType);
      if (parsed.minReviewCount != null) setMinReviewCount(parsed.minReviewCount);
      if (parsed.minReviewScore != null) setMinReviewScore(parsed.minReviewScore);
      if (parsed.userCurrency) setUserCurrency(parsed.userCurrency);
      if (parsed.advancedFilters) setAdvancedFilters(parsed.advancedFilters);

      // Queue auto-run once required state has settled (destination + date)
      if (destCity && parsed.departureDate) {
        pendingAutoRunRef.current = {
          searchType: parsed.searchType || "flightsAndStays",
          advancedFilters: parsed.advancedFilters || {},
          currency: parsed.userCurrency || userCurrency || "USD",
        };
      }
    },
    [citiesData, setUserCurrency, userCurrency]
  );

  // One-time hydration from URL query string on mount
  useEffect(() => {
    if (hydratedFromUrlRef.current) return;
    hydratedFromUrlRef.current = true;
    const parsed = parseSearchParams(window.location.search);
    if (parsed.originKey || parsed.destinationKey) {
      applySearchFromParams(parsed);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire the queued auto-run once destination + departure date have landed in state
  useEffect(() => {
    const pending = pendingAutoRunRef.current;
    if (!pending) return;
    if (!destination || !departureDate) return;
    pendingAutoRunRef.current = null;
    handleSearch(
      pending.searchType,
      pending.advancedFilters,
      null,
      null,
      null,
      pending.currency
    );
  }, [origin, destination, departureDate, returnDate, handleSearch]);

  // Reflect every committed search into the URL (replace, no history entry)
  useEffect(() => {
    if (!searchInitiated) return;
    pushSearchToUrl();
  }, [
    searchInitiated,
    submittedOrigin,
    submittedDestination,
    submittedDepartureDate,
    submittedReturnDate,
    advancedFilters,
    userCurrency,
    searchType,
    pushSearchToUrl,
  ]);

  // Bridge from Layout user-menu (and any other source) via window events
  useEffect(() => {
    const openHandler = () => setSavedSearchesOpen(true);
    const loadHandler = (e) => {
      const params = e?.detail;
      if (!params) return;
      const qs =
        typeof params === "string"
          ? params
          : new URLSearchParams(params).toString();
      const parsed = parseSearchParams(qs);
      applySearchFromParams(parsed);
      setSavedSearchesOpen(false);
    };
    window.addEventListener("open-saved-searches", openHandler);
    window.addEventListener("load-saved-search", loadHandler);
    return () => {
      window.removeEventListener("open-saved-searches", openHandler);
      window.removeEventListener("load-saved-search", loadHandler);
    };
  }, [applySearchFromParams]);

  // Share + Save actions, passed down to CompactSearchForm
  const handleShareSearch = useCallback(async () => {
    const qs = paramsToQueryString(currentSearchParams());
    const url = `${window.location.origin}${window.location.pathname}${qs ? "?" + qs : ""}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setToast({ message: "Link copied to clipboard", severity: "success" });
      } else {
        // Fallback for browsers without Clipboard API
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setToast({ message: "Link copied to clipboard", severity: "success" });
      }
    } catch (err) {
      setToast({ message: "Couldn't copy link", severity: "error" });
    }
  }, [currentSearchParams]);

  // Fetch the current user's (non-expired) saved searches into the local index.
  // Called on mount, when isPro flips, after save/unsave, and when the dialog
  // dispatches "saved-searches-changed".
  const refreshSavedSearchIndex = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSavedSearchIndex([]);
        return;
      }
      const todayYMD = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("saved_searches")
        .select("id, search_params")
        .eq("user_id", user.id)
        .gte("departure_date", todayYMD);
      if (error) {
        logger.warn?.("saved_searches refresh error", error);
        setSavedSearchIndex([]);
        return;
      }
      setSavedSearchIndex(
        (data || []).map((row) => ({
          id: row.id,
          canonicalKey: canonicalKeyFromParams(row.search_params),
        }))
      );
    } catch (err) {
      logger.warn?.("refreshSavedSearchIndex failed", err);
      setSavedSearchIndex([]);
    }
  }, []);

  // Initial load + reload when sign-in / pro status changes
  useEffect(() => {
    if (isPro) refreshSavedSearchIndex();
    else setSavedSearchIndex([]);
  }, [isPro, refreshSavedSearchIndex]);

  // Listen for changes from SavedSearchesDialog (delete, etc.)
  useEffect(() => {
    const handler = () => refreshSavedSearchIndex();
    window.addEventListener("saved-searches-changed", handler);
    return () => window.removeEventListener("saved-searches-changed", handler);
  }, [refreshSavedSearchIndex]);

  // Is the *current* search inputs already saved? If so, expose its row id.
  const currentSavedSearchId = useMemo(() => {
    const key = canonicalKeyFromState({
      origin,
      destination,
      departureDate,
      returnDate,
      adults,
      children,
      flightType,
      airbnbType,
      searchType,
      minReviewCount,
      minReviewScore,
      userCurrency,
      advancedFilters,
    });
    if (!key) return null;
    return savedSearchIndex.find((s) => s.canonicalKey === key)?.id || null;
  }, [
    savedSearchIndex,
    origin,
    destination,
    departureDate,
    returnDate,
    adults,
    children,
    flightType,
    airbnbType,
    searchType,
    minReviewCount,
    minReviewScore,
    userCurrency,
    advancedFilters,
  ]);

  const isSearchSaved = !!currentSavedSearchId;

  // Toggle: save if not saved, unsave if already saved.
  const handleSaveSearch = useCallback(async () => {
    if (!isPro) {
      setLimitDialog({ feature: "SavedSearch", limit: 0 });
      return;
    }
    if (!destination || !departureDate) {
      setToast({ message: "Pick a destination and a departure date first", severity: "info" });
      return;
    }
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setToast({ message: "Please sign in to save a search", severity: "info" });
        return;
      }

      if (currentSavedSearchId) {
        // Already saved → delete
        const { error } = await supabase
          .from("saved_searches")
          .delete()
          .eq("id", currentSavedSearchId);
        if (error) {
          logger.error?.("saved_searches delete error", error);
          setToast({ message: "Couldn't remove — try again", severity: "error" });
          return;
        }
        setToast({ message: "Removed from saved", severity: "success" });
      } else {
        // Not saved → insert
        const params = currentSearchParams();
        const label = describeSearch({ origin, destination, departureDate, returnDate });
        const { error } = await supabase.from("saved_searches").insert({
          user_id: user.id,
          label,
          search_params: params,
          departure_date: params.dd,
        });
        if (error) {
          logger.error?.("saved_searches insert error", error);
          setToast({ message: "Couldn't save — try again", severity: "error" });
          return;
        }
        setToast({ message: "Search saved", severity: "success" });
      }

      await refreshSavedSearchIndex();
    } catch (err) {
      logger.error?.("handleSaveSearch failed", err);
      setToast({ message: "Something went wrong", severity: "error" });
    }
  }, [
    isPro,
    destination,
    departureDate,
    returnDate,
    origin,
    currentSavedSearchId,
    currentSearchParams,
    refreshSavedSearchIndex,
  ]);

  useEffect(() => {
    if (searchInitiated) {
      logger.log(
        `🔄 User currency changed to ${userCurrency}. Re-initiating search...`
      );
      handleSearch(
        searchType,
        advancedFilters,
        destination,
        null,
        null,
        userCurrency
      );
    }
  }, [userCurrency]);

  const handleCityClick = useCallback(
    async (city, skipScroll = false) => {
      // 👈 1. Add skipScroll parameter (default false)

      // 2. Only scroll if we are NOT skipping (i.e. manual user clicks)
      if (!skipScroll && resultsRef.current) {
        resultsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      const key = `${city.city},${city.country}`;
      const prefetchedStays = prefetchedExploreStays[key] || null;
      const prefetchedFlights = prefetchedExploreFlights[key] || null;

      const newDest = { city: city.city, country_name: city.country, country: city.country };
      setDestination(newDest);
      setDestinationInput(`${city.city}, ${city.country}`);
      setIsExploring(true);

      if (!prefetchedFlights) {
        setFlightResults([]);
        if (tofly || exploreFlightPrefs) {
          setFlightLoading(true);
        }
      } else {
        setFlightResults(prefetchedFlights);
        setFlightLoading(false);
      }
      
      const searchTypeToUse = (tofly || exploreFlightPrefs) ? "flightsAndStays" : "staysOnly";

      await handleSearch(
        searchTypeToUse,
        advancedFilters,
        newDest,
        prefetchedStays,
        null,
        userCurrency,
        true,
        prefetchedFlights
      );
    },
    [
      prefetchedExploreStays,
      prefetchedExploreFlights,
      handleStaySearch,
      advancedFilters,
      setDestination,
      setDestinationInput,
      setIsExploring,
      setSearchType,
      setFlightResults,
      setFlightLoading,
      handleSearch,
      exploreFlightPrefs,
      tofly,
      userCurrency,
    ]
  );

  useEffect(() => {
    if (searchType === "explore" && exploreResults.length > 0 && !isExploring) {
      handleCityClick(exploreResults[0], true);
    }
  }, [exploreResults, searchType, isExploring, handleCityClick]);

  // Empty useEffect (can be removed if not needed)
  useEffect(() => {}, [economyListings, standardListings, premiumListings]);

  // Handle back to search
  const handleBackToSearch = (callback) => {
    setIsExploring(false);
    setFlightResults([]);
    setLoading(false); // Reset loading state
    setSearchInitiated(false); // Return to the search form view
    setNoFlightsFound(false); // Reset no flights state

    if (origin) {
      setOriginInput(`${origin.name} (${origin.iata})`);
    }
    if (destination) {
      setDestinationInput(`${destination.name} (${destination.iata})`);
    }

    // Safely invoke the callback if it exists
    if (typeof callback === "function") {
      callback();
    }
  };

  const handleExploreFlightSearch = (city) => {
    logger.log("🎯 Triggering flight search to:", city.city, city.country);
    // ✅ FIX IS HERE: Pass userCurrency as the 6th argument
    handleSearch(
      "explore",
      {},
      city,
      [],
      null,
      userCurrency // 👈 ✅ MUST PASS userCurrency HERE!
    );
  };

  const handleExploreAgain = useCallback(async () => {
    setSearchInitiated(true);
				setNoFlightsFound(false);
    setExploreLoading(true);
    try {
      const res = await fetch("/api/explore", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          /* whatever payload your backend needs,
           e.g. filters: advancedFilters */
          filters: advancedFilters,
        }),
      });
      const { suggestions } = await res.json();
      setExploreResults(suggestions);
    } catch (err) {
      logger.error("Re-explore failed:", err);
    } finally {
      setExploreLoading(false);
    }
  }, [
    advancedFilters,
    setExploreResults,
    setExploreLoading,
    setSearchInitiated,
  ]);

  return (
    <>
      <noscript>
        <div>
          <h1>FlyBnB: Flights & Airbnb Costs in One Smart Search</h1>
          <p>
            Plan trips effortlessly by combining flight prices and Airbnb
            listings. Quickly filter and find the best deals for your travel
            needs.
          </p>
        </div>
      </noscript>
      <Helmet>
        <title>FlyBnB: Flights & Airbnb Costs in One Smart Search</title>
        <link rel="canonical" href="https://flybnb.io" />
        <meta
          name="description"
          content="Plan your entire trip in one search. Compare flights and Airbnb stays together before you book."
        />
        <meta
          name="keywords"
          content="Flights, Airbnb, Travel, Trip Planning"
        />
      </Helmet>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/* Hero gradient band — only shown before search */}
        <Box sx={(theme) => ({
          display: (searchInitiated || loading) ? "none" : "block",
          background: theme.palette.mode === "light"
            ? "linear-gradient(180deg, #ffffff 0%, #FFF3EE 60%, #FFE8DC 100%)"
            : "transparent",
          width: "100%",
          overflow: "hidden",
          pt: { xs: 3, md: 0 },
          pb: { xs: 3, md: 6 },
        })}>
        <Container
          maxWidth="lg"
          sx={{
            textAlign: "center",
            minHeight: "50vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            color: "text.primary",
            mt: 3,
            mb: 5,
            px: { xs: 0, sm: 2 },
          }}
        >
          {!loading && !searchInitiated && (
            <SearchForm
              originOptions={originOptions}
              originInput={originInput}
              setOriginInput={setOriginInput}
              origin={origin}
              setOrigin={setOrigin}
              destinationOptions={destinationOptions}
              destinationInput={destinationInput}
              setDestinationInput={setDestinationInput}
              destination={destination}
              setDestination={setDestination}
              departureDate={departureDate}
              setDepartureDate={setDepartureDate}
              returnDate={returnDate}
              setReturnDate={setReturnDate}
              adults={adults}
              setAdults={setAdults}
              children={children} // Add children prop
              setChildren={setChildren} // Add setChildren function
              //filterAirports={filterAirports}
              filterCities={filterCities}
              CustomPickersDay={CustomPickersDayStyled}
              flightType={flightType} // Add this line
              setFlightType={setFlightType} // Add this line
              airbnbType={airbnbType}
              setAirbnbType={setAirbnbType}
              searchType={searchType}
              setSearchType={setSearchType}
              minReviewCount={minReviewCount}
              setMinReviewCount={setMinReviewCount}
              minReviewScore={minReviewScore}
              setMinReviewScore={setMinReviewScore}
              handleSearch={handleSearch}
              handleExploreSearch={handleExploreSearch}
              advancedFilters={advancedFilters}
              setAdvancedFilters={setAdvancedFilters}
              setExploreLoading={setExploreLoading}
              setExploreResults={setExploreResults}
              setSearchInitiated={setSearchInitiated}
              setExploreFlightPrefs={setExploreFlightPrefs}
              transportMode={transportMode}
              setTransportMode={setTransportMode}
              travelTime={travelTime}
              setTravelTime={setTravelTime}
              onSearch={handleExploreSearch}
              preferences={preferences}
              setPreferences={setPreferences}
              aiDescription={aiDescription}
              setAiDescription={setAiDescription}
              setToFly={setToFly} // Add this line
              tofly={tofly} // Add this line
              filtersOpen={filtersOpen}
              setFiltersOpen={setFiltersOpen}
              userCurrency={userCurrency} // ✅ add this
              setUserCurrency={setUserCurrency}
              destinationVibe={destinationVibe}
              setDestinationVibe={setDestinationVibe}
            />
          )}
        </Container>
        </Box>{/* end hero gradient band */}

        {/* Loading state — outside gradient band, no coloured background */}
        {loading && searchType !== "explore" && !isExploring && (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "60vh", gap: 3 }}>
            {/* Animated logo / spinner stack */}
            <Box sx={{ position: "relative", width: 90, height: 90, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress
                size={90} thickness={2}
                sx={{
                  position: "absolute",
                  color: "transparent",
                  "& .MuiCircularProgress-circle": {
                    stroke: "url(#loadingGradient)",
                  },
                }}
              />
              {/* inline SVG gradient definition */}
              <svg width={0} height={0} style={{ position: "absolute" }}>
                <defs>
                  <linearGradient id="loadingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#07a1fc" />
                    <stop offset="100%" stopColor="#FF5A5F" />
                  </linearGradient>
                </defs>
              </svg>
              <Typography sx={{ fontSize: "2rem" }}>
                {searchType === "staysOnly" ? "🏠" : searchType === "flightsAndStays" ? "✈️🏠" : "✈️"}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 0.5 }}>
                {searchType === "staysOnly" ? "Finding your stay…" : "Searching flights & stays…"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Scanning live prices across the web
              </Typography>
            </Box>
          </Box>
        )}

        {/* Post-search content — outside gradient band */}
        {!loading && searchInitiated && (
            <Box>
              {!exploreLoading &&
                searchType !== "explore" &&
                !isExploring && (
                  <Box sx={{ maxWidth: "1124px", mx: "auto", px: { xs: 2, md: 0 }, pt: 2, pb: 1, width: "100%" }}>
                  <CompactSearchForm
                    origin={origin}
                    setOrigin={setOrigin}
                    originInput={originInput}
                    setOriginInput={setOriginInput}
                    originOptions={originOptions}
                    destination={destination}
                    setDestination={setDestination}
                    destinationInput={destinationInput}
                    setDestinationInput={setDestinationInput}
                    destinationOptions={destinationOptions}
                    departureDate={departureDate}
                    setDepartureDate={setDepartureDate}
                    returnDate={returnDate}
                    setReturnDate={setReturnDate}
                    adults={adults}
                    setAdults={setAdults}
                    children={children}
                    setChildren={setChildren}
                    handleSearch={handleSearch}
                    flightType={flightType}
                    setFlightType={setFlightType}
                    handleBackToSearch={handleBackToSearch}
                    searchType={searchType}
                    advancedFilters={advancedFilters}
                    setAdvancedFilters={setAdvancedFilters}
                    airbnbType={airbnbType}
                    setAirbnbType={setAirbnbType}
                    filtersOpen={filtersOpen}
                    setFiltersOpen={setFiltersOpen}
                    userCurrency={userCurrency}
                    setUserCurrency={setUserCurrency}
                  />
                  </Box>
                )}

              {exploreLoading && searchType === "explore" && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "50vh",
                    mt: 4,
                    mb: 4,
                  }}
                >
                  <Box sx={{ position: "relative", width: 90, height: 90, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress
                      size={90} thickness={2}
                      sx={{
                        position: "absolute",
                        color: "transparent",
                        "& .MuiCircularProgress-circle": {
                          stroke: "url(#loadingGradient)",
                        },
                      }}
                    />
                    <Typography sx={{ fontSize: "2rem" }}>
                      🔭
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ mt: 5, fontFamily: "'Roboto', sans-serif", textAlign: "center" }}
                  >
                    Exploring new destinations...
                  </Typography>
                </Box>
              )}
              {(searchType === "explore" || isExploring) &&
                exploreResults.length > 0 && (
                  <Box
                    ref={cityCarouselRef}
                    sx={{
                      mt: 0,
                      mb: 4,
                      width: "100%",
                      maxWidth: "1124px",
                      mx: "auto",
                      px: { xs: 2, md: 0 },
                    }}
                  >
                    <ExploreCompactSearchForm
                      origin={origin}
                      setOrigin={setOrigin}
                      originInput={originInput}
                      setOriginInput={setOriginInput}
                      originOptions={originOptions}
                      destination={destination}
                      setDestination={setDestination}
                      destinationInput={destinationInput}
                      setDestinationInput={setDestinationInput}
                      destinationOptions={destinationOptions}
                      departureDate={departureDate}
                      setDepartureDate={setDepartureDate}
                      returnDate={returnDate}
                      setReturnDate={setReturnDate}
                      handleSearch={handleSearch}
                      advancedFilters={advancedFilters}
                      setAdvancedFilters={setAdvancedFilters}
                      airbnbType={airbnbType}
                      setAirbnbType={setAirbnbType}
                      travelMode={transportMode}
                      onTravelModeChange={setTransportMode}
                      travelTime={travelTime}
                      setTravelTime={setTravelTime}
                      preferences={preferences}
                      setPreferences={setPreferences}
                      aiDescription={aiDescription}
                      setAiDescription={setAiDescription}
                      destinationVibe={destinationVibe}
                      setDestinationVibe={setDestinationVibe}
                      onSearch={handleExploreSearch}
                      searchType={searchType}
                      setSearchType={setSearchType}
                      handleBackToSearch={handleBackToSearch}
                      filtersOpen={filtersOpen}
                      setFiltersOpen={setFiltersOpen}
                      userCurrency={userCurrency}
                      setUserCurrency={setUserCurrency}
                      onFiltersClick={() => setFiltersOpen(true)}
                    />
                    <ExploreCityCarousel
                      exploredCities={exploreResults}
                      onCityClick={(city) => {
                        setSelectedExploreCity(city);
                        handleCityClick(city);
                      }}
                      selectedCity={selectedExploreCity}
                      mediansByCity={prefetchedExploreMedians}
                      prefetchedExploreStays={prefetchedExploreStays}
                      onExploreMore={() => {
                        const currentCityNames = exploreResults
                          .map((c) => c.city)
                          .join(", ");
                        handleExploreSearch({
                          origin,
                          departureDate,
                          travelTime,
                          transportMode,
                          preferences,
                          destinationVibe,
                          description: `${aiDescription || ""} (IMPORTANT: Do NOT suggest these cities again: ${currentCityNames})`,
                        });
                      }}
                      transportMode={transportMode}
                      onFlightSearch={handleExploreFlightSearch}
                      tofly={tofly}
                      departureDate={departureDate}
                      returnDate={returnDate}
                      handleBackToSearch={handleBackToSearch}
                      currency={userCurrency}
                    />
                  </Box>
                )}

              {/* Total Results Cards Top */}
              {!exploreLoading && (
                <Box
                  ref={resultsRef}
                  sx={{
                    width: "100%",
                    maxWidth: "1200px",
                    mx: "auto",
                    px: 2,
                    overflowX: "hidden",
                    py: 1,
                  }}
                >
                  <Results
                    flightResults={flightResults}
                    flightLoading={flightLoading}
                    bestItinerary={(flightResults || []).find((f) => f.isBest)}
                    cheapestItinerary={(flightResults || []).find(
                      (f) => f.isCheapest
                    )}
                    fastestItinerary={(flightResults || []).find(
                      (f) => f.isFastest
                    )}
                    sortBy={sortBy}
                    handleSortChange={handleSortChange}
                    generateSkyScannerLink={generateSkyScannerLink}
                    quartiles={quartiles}
                    selectedStayCategory={selectedStayCategory}
                    handleStayCategoryChange={handleStayCategoryChange}
                    economyListings={economyListings}
                    standardListings={standardListings}
                    premiumListings={premiumListings}
                    handleBackToSearch={handleBackToSearch}
                    departureDate={departureDate}
                    returnDate={returnDate}
                    handleClearStayFilter={handleClearStayFilter}
                    loading={loading}
                    airbnbType={airbnbType}
                    noAirbnbFound={noAirbnbFound}
                    noFlightsFound={noFlightsFound}
                    submittedOrigin={submittedOrigin}
                    submittedDestination={submittedDestination}
                    originIATA={originIATA}
                    destIATA={destIATA}
                    searchType={searchType}
                    minReviewCount={minReviewCount}
                    minReviewScore={minReviewScore}
                    exploreResults={exploreResults}
                    stayLoading={stayLoading}
                    tofly={tofly}
                    origin={origin}
                    setAirbnbType={setAirbnbType}
                    setMinReviewCount={setMinReviewCount}
                    setMinReviewScore={setMinReviewScore}
                    advancedFilters={advancedFilters}
                    setAdvancedFilters={setAdvancedFilters}
                    currency={userCurrency}
                    setFiltersOpen={setFiltersOpen}
                    isPro={isPro}
                    flightPriceHistory={flightPriceHistory}
                    flightType={flightType}
                    enrichReturn={enrichItineraryReturn}
                    onShareSearch={handleShareSearch}
                    onSaveSearch={handleSaveSearch}
                    isSearchSaved={isSearchSaved}
                  />
                </Box>
              )}
            </Box>
          )}

        {!loading && !searchInitiated && (
          <>
            {/* Add Divider here */}
            <Divider sx={{ my: 1, borderBottomWidth: 0, color: "#272727" }} />

            <Container sx={{ mt: 1, mb: 5 }}>
              <Explainer />
            </Container>

            <Subscribe />

            <Divider sx={{ my: 1, borderBottomWidth: 0, color: "#272727" }} />

            <div
              id="contact"
              inert={!loading && !searchInitiated ? undefined : true}
            >
              <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
                <ContactForm />
              </Container>
            </div>
          </>
        )}{" "}
      </LocalizationProvider>
      {limitDialog && (
        <ProUpgradeDialog
          open={!!limitDialog}
          onClose={() => setLimitDialog(null)}
          feature={limitDialog.feature}
          limit={limitDialog.limit}
        />
      )}
      <SavedSearchesDialog
        open={savedSearchesOpen}
        onClose={() => setSavedSearchesOpen(false)}
        isPro={isPro}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: { xs: 24, md: 40 } }}
      >
        {toast ? (() => {
          const sev = toast.severity || "info";
          const isDark = theme.palette.mode === "dark";
          const msg = (toast.message || "").toLowerCase();
          const ToastIcon =
            sev === "error"
              ? ErrorOutlineRoundedIcon
              : sev === "info"
              ? InfoOutlinedIcon
              : msg.includes("copied") || msg.includes("link")
              ? LinkRoundedIcon
              : msg.includes("saved")
              ? BookmarkRoundedIcon
              : CheckCircleRoundedIcon;
          const accent =
            sev === "error"
              ? "linear-gradient(135deg, #e25c5c 0%, #b13b3b 100%)"
              : sev === "info"
              ? "linear-gradient(135deg, #7e78d2 0%, #5F5AA2 100%)"
              : isDark
              ? "linear-gradient(135deg, #6e8efb 0%, #a777e3 100%)"
              : "linear-gradient(135deg, #5F5AA2 0%, #7e78d2 100%)";
          return (
            <Box
              role="alert"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: { xs: "auto", sm: 320 },
                maxWidth: 440,
                pl: 1,
                pr: 1,
                py: 1,
                borderRadius: "999px",
                border: `1px solid ${
                  isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
                }`,
                bgcolor: isDark
                  ? "rgba(22,22,28,0.92)"
                  : "rgba(255,255,255,0.96)",
                backdropFilter: "saturate(180%) blur(14px)",
                WebkitBackdropFilter: "saturate(180%) blur(14px)",
                boxShadow: isDark
                  ? "0 18px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(126,120,210,0.18) inset"
                  : "0 18px 50px rgba(95,90,162,0.18), 0 0 0 1px rgba(95,90,162,0.06) inset",
                animation: "flybnb-toast-in 240ms cubic-bezier(0.2,0.9,0.3,1.2)",
                "@keyframes flybnb-toast-in": {
                  from: { transform: "translateY(8px) scale(0.97)", opacity: 0 },
                  to: { transform: "translateY(0) scale(1)", opacity: 1 },
                },
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  flexShrink: 0,
                  boxShadow: "0 6px 14px rgba(95,90,162,0.35)",
                }}
              >
                <ToastIcon sx={{ fontSize: 19 }} />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  color: isDark ? "rgba(255,255,255,0.95)" : "#1a1a1a",
                  flex: 1,
                  pr: 0.5,
                }}
              >
                {toast.message}
              </Typography>
              <MuiIconButton
                onClick={() => setToast(null)}
                size="small"
                aria-label="dismiss"
                sx={{
                  color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)",
                  "&:hover": {
                    color: isDark ? "#fff" : "#000",
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </MuiIconButton>
            </Box>
          );
        })() : undefined}
      </Snackbar>
    </>
  );
};

export default Home;
